// The client-backed source: the consumer supplies the complete set; the table searches, filters, sorts,
// pages, sums and counts eligibility over it. While the set loads or fails nothing has arrived, so the
// page is not clamped and Retry returns to the same page.

import { useMemo } from 'react'

import { productLocale } from '../format'
import { runQuery } from '../query/run'
import { clampPage, pageRows, shownRange } from '../query/paging'
import { eligibleCount, summarize } from '../query/summary'

/**
 * @param {object} input
 * @param {object[]} input.rows the complete set, used only when status is 'ready'
 * @param {'loading'|'error'|'ready'} input.status
 * @param {object[]} input.columns
 * @param {import('../types').TableState} input.state
 * @param {?object} [input.selection] the normalized selection config
 * @param {string} [input.locale]
 * @returns {import('./request').SourceResult}
 */
export const deriveClient = ({
  rows,
  status,
  columns,
  state,
  selection = null,
  locale,
}) => {
  const ready = status === 'ready'
  const data = ready && Array.isArray(rows) ? rows : []
  const hidden = (state.view && state.view.hidden) || []
  const matching = runQuery(data, columns, state.query, productLocale(locale), {
    hidden,
  })
  const total = matching.length
  const currentPage = clampPage(state.page, total, state.pageSize, ready)
  return {
    mode: 'client',
    status,
    data,
    matching,
    pageRows: pageRows(matching, currentPage, state.pageSize),
    total: ready ? total : null,
    eligibleTotal: ready ? eligibleCount(matching, selection) : null,
    aggregates: ready ? summarize(matching, columns) : {},
    currentPage,
    ...shownRange(ready ? total : 0, currentPage, state.pageSize),
  }
}

/**
 * @param {object} input see deriveClient, plus `onRetry`, the consumer's reload of the complete set
 */
export const useClientSource = ({
  rows,
  status,
  columns,
  state,
  selection,
  locale,
  onRetry,
}) => {
  const { query, page, pageSize, view } = state
  const hidden = view && view.hidden
  const result = useMemo(
    () =>
      deriveClient({
        rows,
        status,
        columns,
        state: { query, page, pageSize, view: { hidden } },
        selection,
        locale,
      }),
    [rows, status, columns, query, page, pageSize, hidden, selection, locale]
  )
  return { ...result, retry: () => onRetry && onRetry() }
}
